/**
 * @fileoverview Teacher's "View deliverable" drawer (AR-924): three layouts chosen by `submissionMode`,
 * data and writes via `useTeacherDeliverable`. The owner remounts it per open (React `key`); closing is guarded.
 */

import { useState } from 'react'
import { Center, Loader, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { AddLinkModal } from './AddLinkModal'
import { AssessNowFooter } from './teacher/AssessNowFooter'
import { DeliverableContent } from './teacher/DeliverableContent'
import { TeacherDrawerShell } from './teacher/TeacherDrawerShell'
import { submissionMode } from './teacher/submissionMode'
import { useLeaveGuard, type LeaveReason } from './teacher/useLeaveGuard'
import { useTeacherDeliverable } from './teacher/useTeacherDeliverable'

const LOADING_HEIGHT = 200

interface TaskTrackerDeliverableDrawerProps {
  opened: boolean
  sessionMeetingActivityId: string
  studentId: string
  studentFullName: string
  /** A finished project is read-only on the backend ("Project is finished"). */
  projectFinished?: boolean
  onClose: () => void
  /** Fired after every successful write — the owner refreshes the matrix. */
  onChanged?: () => void
  onAssess?: (lessonId: string) => void
}

function leaveReasonOf(uploading: boolean, dateDirty: boolean): LeaveReason | null {
  if (uploading) return 'upload_in_progress'
  if (dateDirty) return 'unsaved_date'
  return null
}

function LoadingState() {
  return (
    <Center sx={{ height: LOADING_HEIGHT }}>
      <Loader size="md" />
    </Center>
  )
}

function ErrorState() {
  const { t } = useTranslation()
  return (
    <Center sx={{ height: LOADING_HEIGHT }}>
      <Text color="dimmed">{t('task_tracker.deliverable_drawer.error_loading')}</Text>
    </Center>
  )
}

export function TaskTrackerDeliverableDrawer(props: TaskTrackerDeliverableDrawerProps) {
  const { opened, sessionMeetingActivityId, studentId, studentFullName, projectFinished = false } = props
  const { onClose, onChanged, onAssess } = props
  const { t } = useTranslation()
  const state = useTeacherDeliverable({ activityId: sessionMeetingActivityId, studentId, opened, onChanged })
  const { data } = state
  const mode = submissionMode(data)
  const firstName = data?.student.firstName || studentFullName.split(' ')[0]

  const [linkModalOpened, setLinkModalOpened] = useState(false)
  // Date chosen before the first upload — saved by a PATCH right after that upload creates the delivery.
  // Without it the backend keeps the date null and reports the upload on time.
  const [pickedDate, setPickedDate] = useState<string | null>(null)
  const [dateDirty, setDateDirty] = useState(false)

  const isUploading = state.uploadPercent !== null
  const requestClose = useLeaveGuard({
    reason: leaveReasonOf(isUploading, dateDirty),
    onLeave: state.cancelUpload,
    onClose,
  })

  function upload(files: File[]) {
    // Only the first upload saves the picked date; later ones keep the saved one.
    const isFirstUpload = mode === 'not_submitted'
    const workCompletedAt = isFirstUpload && pickedDate ? pickedDate : undefined
    void state.upload(files, workCompletedAt)
  }

  const canAssess = mode === 'teacher' && Boolean(onAssess) && Boolean(data)
  function handleAssess() {
    if (data) onAssess?.(data.sessionMeeting.id)
  }
  const footer = canAssess ? <AssessNowFooter studentFirstName={firstName} onAssess={handleAssess} /> : undefined

  let content
  if (state.loading) content = <LoadingState />
  else if (state.errored || !data) content = <ErrorState />
  else {
    content = (
      <DeliverableContent
        data={data}
        mode={mode}
        state={state}
        studentId={studentId}
        studentFirstName={firstName}
        projectFinished={projectFinished}
        pickedDate={pickedDate}
        onPickDate={setPickedDate}
        onDateEditingChange={setDateDirty}
        onFiles={upload}
        onAddLink={() => setLinkModalOpened(true)}
      />
    )
  }

  return (
    <TeacherDrawerShell
      opened={opened}
      title={t('task_tracker.deliverable_drawer.title')}
      subtitle={t('task_tracker.deliverable_drawer.student', { name: studentFullName })}
      onRequestClose={requestClose}
      footer={footer}
    >
      {content}
      <AddLinkModal opened={linkModalOpened} onClose={() => setLinkModalOpened(false)} onAdd={state.addLink} />
    </TeacherDrawerShell>
  )
}
